/**
 * LayoutElementBlock — klocek elementu non-field (heading / alert / divider / ...).
 *
 * F12.N pakiet B Sub-tura 1: wynosi inline JSX z `SystemModuleBuilderPage`
 * (linia ~2886) do osobnego komponentu, identycznie jak `FieldBlock` dla
 * reużywalności w `renderChild` panelu akordeonu.
 *
 * Klocek zawiera:
 *  - DragHandle (absolute left top — żeby nie psuł preview)
 *  - preview elementu (przekazany przez prop; budowany przez
 *    `tryRenderLayoutElement(mode='builder')` w parent)
 *  - delete button (absolute right top, hover-only)
 */

import { type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X } from 'lucide-react';

import { DragHandle } from './DragHandle';

export interface LayoutElementBlockProps {
  /** ID elementu w drzewie layoutu. */
  elementId: string;
  /** Typ elementu (heading / alert / divider / ...) — używany w data-testid. */
  elementType: string;
  preview: ReactNode;
  onRemove: (elementId: string) => void;
  /** Spread z `useSortable.attributes` + `useSortable.listeners` — fallback gdy `sortableContainerId` nie podane. */
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
  /** ID rodzica (sekcja / panel akordeonu) — wpina `useSortable` z source='element'. Pakiet B Sub-2. */
  sortableContainerId?: string;
  /** Podświetlenie ramki gdy blok jest zaznaczony (panel ustawień w prawym aside). */
  selected?: boolean;
  /** Klik na blok → zaznacza go (pokazuje `propertiesEditor` zarejestrowanego typu,
   * jeśli zdefiniowany). `undefined` = builder host nie wspiera selekcji bloków
   * (np. helper-inline scope) — blok renderuje się bez interakcji kliku. */
  onSelect?: () => void;
}

export function LayoutElementBlock({
  elementId,
  elementType,
  preview,
  onRemove,
  dragHandleProps,
  sortableContainerId,
  selected = false,
  onSelect,
}: LayoutElementBlockProps) {
  const { t } = useTranslation();
  const sortable = useSortable({
    id: elementId,
    data: { source: 'element', target: 'section', sectionId: sortableContainerId },
    disabled: !sortableContainerId,
  });
  const style: CSSProperties = sortableContainerId
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.5 : undefined,
      }
    : {};
  const effectiveHandleProps = sortableContainerId
    ? { ...sortable.attributes, ...sortable.listeners }
    : dragHandleProps;
  return (
    <div
      ref={sortableContainerId ? sortable.setNodeRef : undefined}
      style={style}
      onClick={
        onSelect
          ? (e) => {
              // stopPropagation — nie bąbelkuje do `onClick` sekcji (wybór bloku
              // treści jest niezależny od zaznaczenia sekcji, patrz `SystemModuleBuilderPage`).
              e.stopPropagation();
              onSelect();
            }
          : undefined
      }
      className={
        'group/el relative rounded border bg-bg-secondary p-2 transition ' +
        (onSelect ? 'cursor-pointer ' : '') +
        (selected ? 'border-accent-500 shadow' : 'border-border-primary hover:border-accent-300')
      }
      data-testid={`system-module-builder-layout-element-${elementType}-${elementId}`}
    >
      <div className="absolute left-1 top-1 opacity-0 transition group-hover/el:opacity-100">
        <DragHandle
          ariaLabel={t('tenant.systemModuleBuilder.dragLayoutElement', {
            defaultValue: 'Przeciągnij element',
          })}
          dragHandleProps={effectiveHandleProps as HTMLAttributes<HTMLDivElement>}
          className="p-0.5"
        />
      </div>
      {preview}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(elementId);
        }}
        aria-label={t('common.delete')}
        className="absolute right-1 top-1 rounded p-1 text-text-muted opacity-0 transition group-hover/el:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
        data-testid={`system-module-builder-layout-element-${elementId}-remove`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
